import type { Suggestion, SuggestionKind, SuggestionStatus } from "@divespots/contracts";
import { SUGGESTION_KINDS } from "@divespots/contracts";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { api } from "../api/client.ts";
import { Button } from "../components/Button.tsx";
import { formatTimestamp } from "../i18n/format.ts";
import { useLocale } from "../i18n/index.ts";
import { StatusChip, suggestionTitle } from "../suggest/ui.tsx";
import { Field } from "./ui.tsx";

const PAGE = 50;
const STATUSES: SuggestionStatus[] = ["pending", "approved", "rejected", "withdrawn"];

/** How many suggestions wait for review (the badge in the admin navigation). */
export function usePendingCount(enabled: boolean): number | undefined {
  const q = useQuery({
    queryKey: ["admin", "suggestions", "pending-count"],
    queryFn: ({ signal }) =>
      api
        .listSuggestions({ status: "pending", limit: 200 }, signal)
        .then((r) => r.suggestions.length),
    enabled,
    refetchInterval: 60_000,
    retry: false,
  });
  return q.data;
}

/** `/admin/vorschlaege`: the review queue, filterable by status, kind and submitter. */
export function SuggestionsPage() {
  const { t } = useTranslation();
  const locale = useLocale();
  const [status, setStatus] = useState<SuggestionStatus | "all">("pending");
  const [kind, setKind] = useState<SuggestionKind | "all">("all");
  const [submitter, setSubmitter] = useState("");

  const q = useInfiniteQuery({
    queryKey: ["admin", "suggestions", status, kind, submitter],
    queryFn: ({ pageParam, signal }) =>
      api.listSuggestions(
        {
          limit: PAGE,
          ...(status === "all" ? {} : { status }),
          ...(kind === "all" ? {} : { kind }),
          ...(submitter ? { submittedBy: submitter } : {}),
          ...(pageParam ? { before: pageParam } : {}),
        },
        signal,
      ),
    initialPageParam: "",
    getNextPageParam: (last) =>
      last.suggestions.length < PAGE ? undefined : last.suggestions.at(-1)?.createdAt,
  });
  const rows = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.suggestions), [q.data]);

  // submitters seen so far, for the filter (a chosen one stays selectable)
  const [known, setKnown] = useState<Map<string, string>>(new Map());
  const people = useMemo(() => {
    const m = new Map(known);
    for (const s of rows) m.set(s.submittedBy.id, s.submittedBy.name);
    return [...m.entries()];
  }, [rows, known]);

  return (
    <div className="grid gap-6">
      <h1 className="ds-text-title-1 m-0">{t("review.title")}</h1>
      <div className="adm-filters">
        <Field label={t("review.filterStatus")}>
          {({ id }) => (
            <select
              id={id}
              className="adm-input"
              value={status}
              onChange={(e) => setStatus(e.target.value as SuggestionStatus | "all")}
            >
              <option value="all">{t("admin.status.all")}</option>
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {t(`suggest.status.${s}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t("review.filterKind")}>
          {({ id }) => (
            <select
              id={id}
              className="adm-input"
              value={kind}
              onChange={(e) => setKind(e.target.value as SuggestionKind | "all")}
            >
              <option value="all">{t("admin.status.all")}</option>
              {SUGGESTION_KINDS.map((k) => (
                <option key={k} value={k}>
                  {t(`suggest.kind.${k}`)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label={t("review.filterSubmitter")}>
          {({ id }) => (
            <select
              id={id}
              className="adm-input"
              value={submitter}
              onChange={(e) => {
                setKnown(new Map(people));
                setSubmitter(e.target.value);
              }}
            >
              <option value="">{t("admin.status.all")}</option>
              {people.map(([pid, name]) => (
                <option key={pid} value={pid}>
                  {name}
                </option>
              ))}
            </select>
          )}
        </Field>
      </div>

      {q.isPending ? <p role="status">{t("common.loading")}</p> : null}
      {q.isError ? (
        <p className="adm-error" role="alert">
          {t("admin.err.load")}
        </p>
      ) : null}
      {q.isSuccess && rows.length === 0 ? (
        <p className="ds-text-subheadline text-label-secondary m-0">{t("review.empty")}</p>
      ) : null}

      {rows.length > 0 ? (
        <ul className="ds-list adm-list" aria-label={t("review.title")}>
          {rows.map((s: Suggestion) => (
            <li key={s.id} className="adm-spotrow">
              <Link
                className="ds-row adm-spotrow__link"
                to={`/${locale}/admin/vorschlaege/${s.id}`}
              >
                <span className="ds-row__title">
                  <span className="ds-text-headline">{suggestionTitle(s, locale, t)}</span>
                  <span className="ds-text-subheadline text-label-secondary block">
                    {t(`suggest.kind.${s.kind}`)} · {s.submittedBy.name} ·{" "}
                    {formatTimestamp(s.createdAt, locale)}
                  </span>
                </span>
                <StatusChip status={s.status} />
              </Link>
            </li>
          ))}
        </ul>
      ) : null}

      {q.hasNextPage ? (
        <div className="adm-actions">
          <Button
            variant="tinted"
            disabled={q.isFetchingNextPage}
            onClick={() => void q.fetchNextPage()}
          >
            {t("suggest.more")}
          </Button>
        </div>
      ) : null}
    </div>
  );
}
